
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface RevealOnScrollProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right";
  distance?: number;
  once?: boolean;
  threshold?: number;
}

export function RevealOnScroll({
  children,
  className,
  delay = 0,
  direction = "up",
  distance = 100, // Increased from 30 to 100 for more dramatic movement
  once = true,
  threshold = 0.1,
}: RevealOnScrollProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once && currentRef) {
            observer.unobserve(currentRef);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold,
      }
    );
    
    observer.observe(currentRef);
    
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [once, threshold]);
  
  const getDirectionStyles = () => {
    if (!isVisible) {
      switch (direction) {
        case "up":
          return `translate-y-[${distance}px]`;
        case "down":
          return `translate-y-[-${distance}px]`;
        case "left":
          return `translate-x-[${distance}px]`;
        case "right":
          return `translate-x-[-${distance}px]`;
        default:
          return `translate-y-[${distance}px]`;
      }
    }
    return "translate-y-0 translate-x-0";
  };
  
  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-1000 ease-out", // Increased duration from 700ms to 1000ms
        isVisible ? "opacity-100" : "opacity-0",
        getDirectionStyles(),
        className
      )}
      style={{ 
        transitionDelay: `${delay}ms`,
        transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)" // Added spring-like effect
      }}
    >
      {children}
    </div>
  );
}
